import React from 'react';
import useAuth from '../../hooks/useAuth';
import { isAdminOrAbove, isSSLOrAbove, ROLE_LABELS } from '../../utils/roles';

const StatCard = ({ label, value, sub }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
    <p className="text-sm text-gray-500">{label}</p>
    <p className="mt-1 text-3xl font-bold text-gray-900">{value}</p>
    {sub && <p className="mt-1 text-xs text-gray-400">{sub}</p>}
  </div>
);

export default function DashboardPage() {
  const { user } = useAuth();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {user?.first_name}
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {ROLE_LABELS[user?.role]} · {user?.district ? `District ${user.district}` : 'All Districts'}
        </p>
      </div>

      {/* SL stats */}
      {!isSSLOrAbove(user) && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard label="Weekday Shifts"  value="—" sub="this semester" />
          <StatCard label="Weekend Shifts"  value="—" sub="this semester" />
          <StatCard label="Heavy Days"      value="—" sub="this semester" />
          <StatCard label="Sitter Roles"    value="—" sub="this semester" />
        </div>
      )}

      {/* SSL / Admin stats */}
      {isSSLOrAbove(user) && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard label="Total SLs"         value="—" />
          <StatCard label="Pending Swaps"      value="—" sub="awaiting approval" />
          <StatCard label="Availability Open"  value="—" sub="SLs not yet submitted" />
          <StatCard label="Current Period"     value="—" />
        </div>
      )}

      {/* Quick actions */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-3">Quick Actions</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {!isSSLOrAbove(user) && (
            <>
              <ActionCard to="/availability" label="Submit Availability" desc="Fill out your availability for the current period" />
              <ActionCard to="/schedule"     label="View Schedule"       desc="See your upcoming duty shifts" />
              <ActionCard to="/swaps"        label="Swap Marketplace"    desc="Post or claim shift swaps" />
            </>
          )}
          {isSSLOrAbove(user) && (
            <>
              <ActionCard to="/swaps/approvals"  label="Swap Approvals"     desc="Review pending swap requests" />
              <ActionCard to="/schedule/manage"  label="Manage Schedule"    desc="Edit and publish duty schedules" />
              <ActionCard to="/schedule"         label="View Schedules"     desc="View Fire and Ice calendars" />
            </>
          )}
          {isAdminOrAbove(user) && (
            <>
              <ActionCard to="/admin/periods"  label="Manage Periods"   desc="Open availability windows and trigger generation" />
              <ActionCard to="/admin/users"    label="Manage Users"     desc="Add, edit, or deactivate staff" />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ActionCard({ to, label, desc }) {
  return (
    <a
      href={to}
      className="block rounded-xl border border-gray-200 bg-white p-4 shadow-sm hover:border-indigo-300 hover:shadow-md transition-all"
    >
      <p className="font-semibold text-gray-900">{label}</p>
      <p className="mt-1 text-sm text-gray-500">{desc}</p>
    </a>
  );
}
